import { Outlet, Navigate, createRootRoute, useRouterState } from "@tanstack/react-router";
import { ApiQueryServiceProvider } from "@/api/ApiQueryServiceProvider";
import { HeaderComponent } from "@/components/layout/header";
import { FooterComponent } from "@/components/layout/footer";
import { SideNavComponent } from "@/components/layout/sideNav";
import { SidebarProvider } from "@/components/ui/sidebar";
import { SecurityProvider } from "@/contexts/SecurityContext";
import { useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/toaster";
import { SecurityVerifier } from "@/security/routeSecurityVerifier";
import { ErrorBoundary } from "@/components/ErrorBoundary";

export const Route = createRootRoute({
  component: RootComponent,
  beforeLoad: async (ctx) => {
    // Enable SecurityVerifier to run
    // Note: Keep isSecured: false for most routes until fully tested
    // Set isSecured: true to enforce route protection based on route staticData.isSecured flags
    await SecurityVerifier(ctx, { isSecured: true });
  },
  notFoundComponent: () => {
    return <Navigate to="/home" />;
  },
});

function RootComponent() {
  const router = useRouterState();
  const currentRoute = router.matches[router.matches.length - 1];
  const isSecured = (currentRoute?.staticData as any)?.isSecured ?? true;
  const { conExJwt } = useAuth();

  return (
    <ApiQueryServiceProvider token={conExJwt ?? undefined}>
      <SecurityProvider>
        {isSecured ? (
          // Layout with frame (header, sidebar, footer) for secured routes
          <SidebarProvider defaultOpen={true} className="w-full overflow-x-clip">
            <div className="fixed inset-x-0 top-0 z-50 h-16">
              <HeaderComponent />
            </div>
            <SideNavComponent />
            {/* Main app shell: full-height column with header offset, content, footer */}
            <div className="flex min-h-screen flex-col min-w-0 transition-all duration-300 ease-in-out pt-16 w-full overflow-x-hidden">
              <main className="flex-1 p-6 flex flex-col">
                <div className="w-full max-w-7xl mx-auto flex-1 flex flex-col">
                  <ErrorBoundary>
                    <Outlet />
                  </ErrorBoundary>
                </div>
              </main>
              <FooterComponent />
            </div>
            <Toaster />
          </SidebarProvider>
        ) : (
          // No frame layout for public routes (signin, landing, etc.)
          <div>
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
            <Toaster />
          </div>
        )}
      </SecurityProvider>
      {/* Start rendering router matches */}
    </ApiQueryServiceProvider>
  );
}
